import Head from "next/head";

const SITE_URL = "https://xsiadron.com";
const PERSON_NAME = "\u0141ukasz Frydrych";
const PERSON_ASCII_NAME = "Lukasz Frydrych";
const PERSON_PUBLIC_NAME = "Luke Frydrych";
const SITE_NAME = "xsiadron";
const DEFAULT_TITLE = `xsiadron - ${PERSON_NAME} (${PERSON_PUBLIC_NAME}) | Game, App & Web Developer`;
const DEFAULT_DESCRIPTION =
    "xsiadron.com is the portfolio of \u0141ukasz Frydrych, also known as Luke Frydrych and xsiadron, featuring indie game, app, web, tool, and creative software projects.";
const DEFAULT_KEYWORDS = [
    "xsiadron",
    PERSON_NAME,
    PERSON_ASCII_NAME,
    PERSON_PUBLIC_NAME,
    "Luke xsiadron Frydrych",
    "indie game developer",
    "app developer",
    "web developer",
    "React developer",
    "Next.js developer",
    "Poland developer",
];
const SOCIAL_LINKS = [
    "https://github.com/xsiadron",
    "https://www.youtube.com/@xsiadron",
    "https://www.instagram.com/xsiadron/",
    "https://www.tiktok.com/@xsiadron",
    "https://twitter.com/xsiadron",
    "https://steamcommunity.com/id/xsiadron/",
    "https://useme.com/pl/roles/contractor/xsiadron,329628/",
];

function stripHtml(value = "") {
    return value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function buildStructuredData(projects = [], canonicalUrl, title, description) {
    const projectItems = projects.map((project, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
            "@type": "CreativeWork",
            name: project.title,
            url: project.link || canonicalUrl,
            image: project.image ? `${SITE_URL}${project.image}` : undefined,
            description: stripHtml(project.description),
            datePublished: project.date,
            author: {
                "@id": `${SITE_URL}/#person`,
            },
            keywords: Array.isArray(project.tags) ? project.tags.join(", ") : undefined,
        },
    }));

    return {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "Person",
                "@id": `${SITE_URL}/#person`,
                name: PERSON_NAME,
                alternateName: [SITE_NAME, PERSON_PUBLIC_NAME, PERSON_ASCII_NAME],
                url: SITE_URL,
                email: "mailto:lukasz.frydrych@xsiadron.com",
                jobTitle: "Indie game, app and web developer",
                knowsAbout: [
                    "Game development",
                    "Web development",
                    "App development",
                    "React",
                    "Next.js",
                    "TypeScript",
                    "C++",
                    "C#",
                    "Kotlin",
                    "Java",
                    "Python",
                    "3D modeling",
                    "Digital illustration",
                ],
                sameAs: SOCIAL_LINKS,
            },
            {
                "@type": "WebSite",
                "@id": `${SITE_URL}/#website`,
                name: "xsiadron.com",
                alternateName: [SITE_NAME, PERSON_NAME, PERSON_PUBLIC_NAME, PERSON_ASCII_NAME],
                url: SITE_URL,
                description,
                inLanguage: "en",
                publisher: {
                    "@id": `${SITE_URL}/#person`,
                },
            },
            {
                "@type": "ProfilePage",
                "@id": `${SITE_URL}/#homepage`,
                name: title,
                url: canonicalUrl,
                description,
                inLanguage: "en",
                isPartOf: {
                    "@id": `${SITE_URL}/#website`,
                },
                about: {
                    "@id": `${SITE_URL}/#person`,
                },
                mainEntity: {
                    "@id": `${SITE_URL}/#person`,
                },
            },
            {
                "@type": "ItemList",
                "@id": `${SITE_URL}/#projects`,
                name: "xsiadron projects",
                itemListElement: projectItems,
            },
        ],
    };
}

export default function Seo({
    title = DEFAULT_TITLE,
    description = DEFAULT_DESCRIPTION,
    path = "/",
    image = "/images/AvatarTranslucent.png",
    noindex = false,
    projects = [],
}) {
    const canonicalUrl = `${SITE_URL}${path === "/" ? "/" : path}`;
    const imageUrl = `${SITE_URL}${image}`;
    const structuredData = buildStructuredData(projects, canonicalUrl, title, description);
    const robots = noindex
        ? "noindex, nofollow"
        : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

    return (
        <Head>
            <title>{title}</title>
            <meta name="description" content={description} />
            <meta name="keywords" content={DEFAULT_KEYWORDS.join(", ")} />
            <meta name="robots" content={robots} />
            <meta name="author" content={`${PERSON_NAME} (${PERSON_PUBLIC_NAME})`} />
            <meta name="application-name" content="xsiadron.com" />
            <meta name="theme-color" content="#202020" />
            <link rel="canonical" href={canonicalUrl} />
            <link rel="icon" href="/favicon.ico" sizes="any" />
            <link rel="apple-touch-icon" href="/images/AvatarTranslucent.png" />
            <link rel="manifest" href="/manifest.json" />

            <meta property="og:type" content="website" />
            <meta property="og:site_name" content="xsiadron.com" />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:image" content={imageUrl} />
            <meta property="og:image:width" content="512" />
            <meta property="og:image:height" content="512" />
            <meta property="og:locale" content="en_US" />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={imageUrl} />
            <meta name="twitter:creator" content="@xsiadron" />

            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
            />
        </Head>
    );
}
